import { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/components/common/DataTable';
import { Button } from '@/components/ui/button';
import { WithdrawalEntry } from '@/types';
import {
  useGetTransactionHistoryQuery,
  useUpdateTransactionStatusMutation,
} from '@/store/api/walletTransactionApi';
import { mapBackendStatusToAdmin } from '@/utils/transactionStatusMapper';
import { useState } from 'react';

export default function WithdrawalList() {
  // ✅ Server pagination state
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // ✅ API call (0-based → 1-based)
  const { data, isLoading } = useGetTransactionHistoryQuery({
    page: pageIndex + 1,
    limit: pageSize,
    type: 'Debit',
    status: 'Pending',
  });

  const total = data?.pagination?.totalItems ?? 0;

  const [updateStatus, { isLoading: isUpdating }] = useUpdateTransactionStatusMutation();

  const handleApprove = async (id: number) => {
    await updateStatus({ id, action: 'Approved' }).unwrap();
  };

  const handleReject = async (id: number) => {
    await updateStatus({ id, action: 'Rejected' }).unwrap();
  };

  const withdrawalData: WithdrawalEntry[] =
    data?.data.map((tx) => ({
      id: tx.id.toString(),
      userId: tx.wallet?.userId?.toString() ?? '',
      username: `${tx.wallet.user.firstname} ${tx.wallet.user.lastname}`,
      accountHolder: tx.bank?.accoutnHolder || tx.upi?.name || 'N/A',
      amount: tx.amount,
      accountNumber: tx.bank?.accountNumber ?? tx.upi?.upi ?? '',
      ifscOrUpi: tx.bank?.ifsc ?? tx.upi?.upi ?? '',
      status: mapBackendStatusToAdmin(tx.status),
      requestedAt: tx.createdAt,
    })) ?? [];

  const withdrawalColumns: ColumnDef<WithdrawalEntry>[] = [
    {
      header: 'Sr.',
      cell: ({ row }) => pageIndex * pageSize + row.index + 1, // ✅ global index
    },
    {
      accessorKey: 'username',
      header: 'User Name',
      cell: ({ row }) => (
        <span className="text-blue-500 cursor-pointer hover:underline">
          {row.original.username}
        </span>
      ),
    },
    { accessorKey: 'accountHolder', header: 'Account Holder' },
    {
      accessorKey: 'amount',
      header: 'Amount',
      cell: ({ row }) => `₹${row.original.amount.toLocaleString()}`,
    },
    { accessorKey: 'accountNumber', header: 'Account / Mobile No.' },
    { accessorKey: 'ifscOrUpi', header: 'IFSC Code / UPI ID' },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const s = row.original.status;
        const color =
          s === 'Approved'
            ? 'text-green-600'
            : s === 'Pending'
              ? 'text-yellow-600'
              : 'text-red-600';

        return <span className={color}>{s}</span>;
      },
    },
    {
      accessorKey: 'requestedAt',
      header: 'Requested At',
      cell: ({ row }) => new Date(row.original.requestedAt).toLocaleString('en-IN'),
    },
    {
      header: 'Action',
      cell: ({ row }) => (
        <div className="flex gap-2">
          <Button
            size="sm"
            disabled={row.original.status !== 'Pending' || isUpdating}
            className="bg-green-600 hover:bg-green-700"
            onClick={() => handleApprove(Number(row.original.id))}
          >
            Approve
          </Button>
          <Button
            size="sm"
            variant="destructive"
            disabled={row.original.status !== 'Pending' || isUpdating}
            onClick={() => handleReject(Number(row.original.id))}
          >
            Reject
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold tracking-tight">Withdrawal Requests</h2>
          <p className="text-muted-foreground">Review and manage all withdrawal requests.</p>
        </div>
      </div>

      <DataTable
        columns={withdrawalColumns}
        data={withdrawalData}
        enableSearch
        pageIndex={pageIndex}
        pageSize={pageSize}
        total={total}
        onPageChange={setPageIndex}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPageIndex(0);
        }}
      />
    </div>
  );
}
